import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { RULES_KEYS } from '../../../../utils/constants/rules-stats';
import { IToast } from '../../../../interfaces/components/IToast';
import { tryToFetchRules, tryToUpdateRules } from '../../../../store/brokers/partners/admin/adminSlice';
import RulesSceleton from '../components/RulesSceleton';
import Toast from '../../../../shared/toast';


const Rules = () => {
    const dispatch = useAppDispatch();
    const [rules, setRules] = useState<any>({});
    const [changes, setChanges] = useState<any>({});
    const [showPrevious, setShowPrevious] = useState<Record<string, boolean>>({});
    const [changeError, setChangeError] = useState<any>();
    const [showToast, setShowToast] = useState<IToast | null>();
    const state = useAppSelector(state => state.admin);

    useEffect(() => {
        if (state?.rules) setRules(state.rules);
    }, [state?.rules]);

    const onGettingData = async () => {
        try {
            await dispatch(tryToFetchRules()).unwrap();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        onGettingData();
    }, []);

    const onSaveChanges = async (key: string, secondKey?: any) => {
        try {
            let formData: any = {
                [key]: changes?.[key]
            }
            if (secondKey) {
                if (typeof secondKey === 'string') {
                    if (!changes?.[secondKey]) {
                        setChangeError({ key: `Please check and fill all the details to save` })
                        return
                    }
                    formData = {
                        ...formData || {},
                        [secondKey]: changes?.[secondKey]
                    }
                } else {
                    formData = {}
                    secondKey && secondKey?.length > 0 && secondKey?.some((item: any) => {
                        if (!changes?.[item]) {
                            setChangeError({ key: 'Please check and fill all the details to save' });
                            return true;
                        } else {
                            formData = {
                                ...formData || {},
                                [key]: {
                                    ...(formData?.[key] || {}),
                                    [item]: changes?.[item]
                                }
                            };
                        }
                        return false;
                    });
                }
            }
            setChangeError(undefined)
            setRules((prev: any)=>({
                ...prev || {},
                ...formData || {},
            }))
            await dispatch(tryToUpdateRules(formData)).unwrap();
            setShowToast({ type: 'success', message: 'Rule saved successfully!' });
        } catch (err) {
            setChangeError({ key: err })
        }
    }

    const handleChange = (key: string, value: any) => {
        if (value === rules[key]) {
            setChanges((prev: any) => {
                const newChanges = { ...prev };
                delete newChanges[key];
                return newChanges;
            });
        } else {
            setChanges((prev: any) => ({ ...prev, [key]: value }));
        }
    };

    const handleReapplyPrevious = (ruleKey: string) => {
        setChanges((prev: any) => ({
            ...prev || {},
            [ruleKey]: rules?.[ruleKey]
        }));
    };

    const handleShowPrevious = (ruleKey: string) => {
        setShowPrevious(prev => ({ ...prev, [ruleKey]: !prev[ruleKey] }));
    };

    const formatText = (text: string | null | undefined): string => {
        if (!text) return '';
        return text
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            )
            .join(' ');
    };

    const renderInput = (ruleKey: string, placeholder = '', name?: string) => (
        <div>
            {name && <p className='dark:text-primary-dark'>{name}</p>}
            <input
                value={changes[ruleKey] ?? rules[ruleKey] ?? ''}
                onChange={(e) => handleChange(ruleKey, e.target.value)}
                className='w-full md:w-1/2 border p-2 rounded dark:bg-black dark:text-primary-dark'
                placeholder={formatText(placeholder || '')}
                name={name}
            />
        </div>
    );

    const renderSelect = (
        ruleKey: string,
        options: { value: string; label: string }[],
        allowCustom = false
    ) => {
        const currentValue = changes[ruleKey] ?? rules[ruleKey];

        return (
            <>
                <select
                    className='w-full md:w-1/2 border p-2 rounded mb-2 dark:bg-black dark:text-primary-dark'
                    value={currentValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        handleChange(ruleKey, val);
                    }}
                >
                    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    {allowCustom && <option value='custom'>Custom</option>}
                </select>
                {/* {isCustom && <div className='my-2'>{renderInput(ruleKey)}</div>} */}
            </>
        );
    };

    return (
        <div className='my-8 space-y-6'>
            <RulesSceleton
                ruleKey='matching_rule_type'
                title='Match Criteria'
                error={changeError?.matching_rule_type}
                description='How should we determine a match?'
                previousValue={rules.matching_rule_type}
                changedValue={changes?.matching_rule_type}
                showPrevious={showPrevious?.matching_rule_type}
                onShowPrevious={handleShowPrevious}
                onClickReapplyPrevious={handleReapplyPrevious}
                onClickSave={() => onSaveChanges('matching_rule_type')}
            >
                {renderSelect('matching_rule_type', RULES_KEYS?.matching_rule_type)}
            </RulesSceleton>

            {((rules.matching_rule_type === 'variance_1day' && !changes?.matching_rule_type) || (changes?.matching_rule_type && changes?.matching_rule_type === 'variance_1day')) && (
                <RulesSceleton
                    ruleKey='amount_variance'
                    title='Amount Variance'
                    error={changeError?.amount_variance}
                    description='Allowed variance if amount is not exact.'
                    previousValue={rules?.amount_variance}
                    changedValue={changes?.amount_variance}
                    showPrevious={showPrevious?.amount_variance}
                    onShowPrevious={handleShowPrevious}
                    onClickReapplyPrevious={handleReapplyPrevious}
                    onClickSave={() => onSaveChanges('amount_variance')}
                >
                    {renderInput('amount_variance', 'e.g. ±5 EUR', 'Amount Variance')}
                </RulesSceleton>
            )}

            {((rules.matching_rule_type === 'custom' && !changes?.matching_rule_type) || changes?.matching_rule_type === 'custom') && (
                <RulesSceleton
                    ruleKey='custom_match_config'
                    title='Custom Match Rules'
                    error={changeError?.custom_match_config}
                    description='Define your own matching rules.'
                    previousValue={rules.custom_match_config}
                    changedValue={changes?.custom_match_config}
                    showPrevious={showPrevious?.custom_match_config}
                    onShowPrevious={handleShowPrevious}
                    onClickReapplyPrevious={handleReapplyPrevious}
                    onClickSave={() => onSaveChanges('custom_match_config', ['amount_variance', 'time_variance', 'rule'])}
                >
                    {['amount_variance', 'time_variance', 'rule'].map(field => (
                        <div key={field} className='mb-2'>
                            {renderInput(`custom_match_config.${field}`, field, formatText(field))}
                        </div>
                    ))}
                </RulesSceleton>
            )}

            <RulesSceleton
                ruleKey='stop_matching'
                title='Stop Matching Rule'
                error={changeError?.stop_matching}
                description='When should we stop trying to match a transaction?'
                previousValue={rules.stop_matching}
                changedValue={changes?.stop_matching}
                showPrevious={showPrevious?.stop_matching}
                onShowPrevious={handleShowPrevious}
                onClickReapplyPrevious={handleReapplyPrevious}
                onClickSave={() => onSaveChanges('stop_matching', ((rules?.stop_matching === 'after_x_days' && !changes?.stop_matching) || changes?.stop_matching === 'after_x_days') ? 'stop_matching_after_days' : undefined)}
            >
                {renderSelect('stop_matching', RULES_KEYS?.stop_matching)}
                {((rules?.stop_matching === 'after_x_days' && !changes?.stop_matching) || changes?.stop_matching === 'after_x_days')
                    && <div className='my-2'>{renderInput('stop_matching_after_days', 'e.g. 4', 'Specify Days')}</div>
                }
            </RulesSceleton>

            <RulesSceleton
                ruleKey='discrepancy_handling'
                title='Discrepancy Handling'
                description='How do you want to handle discrepancies?'
                previousValue={rules.discrepancy_handling}
                error={changeError?.discrepancy_handling}
                changedValue={changes?.discrepancy_handling}
                showPrevious={showPrevious?.discrepancy_handling}
                onShowPrevious={handleShowPrevious}
                onClickReapplyPrevious={handleReapplyPrevious}
                onClickSave={() => onSaveChanges('discrepancy_handling', ((rules.discrepancy_handling === 'escalate' && !changes?.discrepancy_handling) || changes.discrepancy_handling === 'escalate') ? 'discrepancy_escalation_threshold' : '')}
            >
                {renderSelect('discrepancy_handling', RULES_KEYS?.discrepancy_handling)}
                {((rules.discrepancy_handling === 'escalate' && !changes?.discrepancy_handling) || changes?.discrepancy_handling === 'escalate')
                    && <div className='my-2'>{renderInput('discrepancy_escalation_threshold')}</div>
                }
            </RulesSceleton>

            <RulesSceleton
                ruleKey='frequency'
                title='Reconciliation Frequency'
                description='How often should reconciliation run?'
                previousValue={rules.frequency}
                error={changeError?.frequency}
                changedValue={changes?.frequency}
                showPrevious={showPrevious?.frequency}
                onShowPrevious={handleShowPrevious}
                onClickReapplyPrevious={handleReapplyPrevious}
                onClickSave={() => onSaveChanges('frequency', ((rules.frequency === 'manual' && !changes?.frequency) || changes.frequency === 'manual') ? 'manual_trigger_schedule' : undefined)}
            >
                {renderSelect('frequency', RULES_KEYS?.frequency)}
                {((rules.frequency === 'manual' && !changes?.frequency) || changes.frequency === 'manual')
                    && <div className='my-2'>{renderInput('manual_trigger_schedule')}</div>
                }
            </RulesSceleton>

            <RulesSceleton
                ruleKey='blue_status_days'
                title='Blue Status Day(s) parameter'
                description='This parameter will define how many days will the systemwait for the PSP transactions'
                previousValue={rules.blue_status_days}
                error={changeError?.blue_status_days}
                changedValue={changes?.blue_status_days}
                showPrevious={showPrevious?.blue_status_days}
                onShowPrevious={handleShowPrevious}
                onClickReapplyPrevious={handleReapplyPrevious}
                onClickSave={() => onSaveChanges('blue_status_days')}
            >
                {renderInput('blue_status_days', 'e.g. 5', 'Set Days')}
            </RulesSceleton>
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
        </div>
    );
};

export default Rules;